import { useState, useEffect } from "react";
import { listConnections } from "@/features/admin/connections/api/client";
import type { ConnectionDto } from "@/features/admin/connections/api/client";
import { listServiceAccounts } from "@/features/admin/form-config/export/api/dossier-lookup-client";
import type { DossierServiceAccount } from "@/features/admin/form-config/export/api/dossier-lookup-client";
import { TemplateInput, DOSSIER_TITLE_TOKENS } from "./TemplateInput";

// ═══════════════════════════════════════════════════════════════
// Adapter Config Forms — Dynamic config editors per adapter type
// ═══════════════════════════════════════════════════════════════

interface AdapterConfigProps {
  adapterType: string;
  config: Record<string, unknown>;
  onChange: (config: Record<string, unknown>) => void;
}

export function AdapterConfigForm({ adapterType, config, onChange }: AdapterConfigProps) {
  switch (adapterType) {
    case "skarn_product":
      return <SkarnProductForm config={config} onChange={onChange} />;
    case "webhook":
      return <WebhookForm config={config} onChange={onChange} />;
    case "filesystem":
      return <FilesystemForm config={config} onChange={onChange} />;
    case "sftp":
      return <SftpForm config={config} onChange={onChange} />;
    case "s3":
      return <S3Form config={config} onChange={onChange} />;
    case "pdf_summary":
      return <PdfSummaryForm config={config} onChange={onChange} />;
    case "email":
      return <EmailForm config={config} onChange={onChange} />;
    case "dossier":
      return <DossierForm config={config} onChange={onChange} />;
    case "document_repository":
      return <DocumentRepositoryForm config={config} onChange={onChange} />;
    case "sql":
      return <SqlForm config={config} onChange={onChange} />;
    default:
      return <div className="tab-placeholder"><span className="tab-placeholder-text">No configuration available for this adapter type</span></div>;
  }
}

// ─── Shared helpers ────────────────────────────────────────────

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="rb-row">
      <label className="rb-label">{label}</label>
      {children}
    </div>
  );
}

function useConfig(initial: Record<string, unknown>, onChange: (c: Record<string, unknown>) => void) {
  const set = (key: string, value: unknown) => {
    onChange({ ...initial, [key]: value });
  };
  const setNested = (parentKey: string, key: string, value: unknown) => {
    const parent = (initial[parentKey] as Record<string, unknown>) ?? {};
    onChange({ ...initial, [parentKey]: { ...parent, [key]: value } });
  };
  return { get: (k: string) => initial[k], set, setNested };
}

// ─── Skarn Product ──────────────────────────────────────────

// Currently-wireable Skarn products for the skarn_product adapter.
// Canonical source is Railway (`railway list --json`) — the conductor
// CLAUDE.md "swarm" table is stale. Today only Books is deployed; Dossier
// is in active local development. Add new entries here when additional
// Skarn products go live on Railway. Tracking row in the deferred-log:
// `62-followup-railway-sync` (auto-sync from Railway, future P3).
const STRATUM_PRODUCTS = [
  { slug: "books", label: "Books" },
  { slug: "dossier", label: "Dossier" },
] as const;

function SkarnProductForm({ config, onChange }: { config: Record<string, unknown>; onChange: (c: Record<string, unknown>) => void }) {
  const c = useConfig(config, onChange);
  const currentProduct = (c.get("product") as string) ?? "";
  const isCanonical = currentProduct === "" || STRATUM_PRODUCTS.some((p) => p.slug === currentProduct);

  return (
    <div className="adapter-config-form">
      <Field label="Product">
        <select className="rb-select" value={currentProduct} onChange={(e) => c.set("product", e.target.value)}>
          <option value="">Select product...</option>
          {STRATUM_PRODUCTS.map((p) => (
            <option key={p.slug} value={p.slug}>{p.label}</option>
          ))}
          {!isCanonical && (
            // Preserve a previously-stored value (e.g., legacy "pipeline") so
            // existing destinations don't lose their setting on render.
            <option value={currentProduct} disabled>
              {currentProduct} (no longer available — pick a current product)
            </option>
          )}
        </select>
      </Field>
      <Field label="Endpoint">
        <input className="rb-input" value={(c.get("endpoint") as string) ?? ""} onChange={(e) => c.set("endpoint", e.target.value)} placeholder="/api/invoices" />
      </Field>
      <Field label="Suite Internal URL">
        <input className="rb-input" value={(c.get("suiteInternalUrl") as string) ?? ""} onChange={(e) => c.set("suiteInternalUrl", e.target.value)} placeholder="http://books:3004" />
      </Field>
      <Field label="Internal Key">
        <input className="rb-input" type="password" value={(c.get("internalKey") as string) ?? ""} onChange={(e) => c.set("internalKey", e.target.value)} />
      </Field>
      <Field label="Include Images">
        <input type="checkbox" checked={!!c.get("includeImages")} onChange={(e) => c.set("includeImages", e.target.checked)} />
      </Field>
    </div>
  );
}

// ─── Webhook ──────────────────────────────────────────────────

function WebhookForm({ config, onChange }: { config: Record<string, unknown>; onChange: (c: Record<string, unknown>) => void }) {
  const c = useConfig(config, onChange);
  const auth = (c.get("auth") as Record<string, unknown>) ?? {};

  return (
    <div className="adapter-config-form">
      <Field label="URL">
        <input className="rb-input" value={(c.get("url") as string) ?? ""} onChange={(e) => c.set("url", e.target.value)} placeholder="https://example.com/webhook" />
      </Field>
      <Field label="Method">
        <select className="rb-select" value={(c.get("method") as string) ?? "POST"} onChange={(e) => c.set("method", e.target.value)}>
          <option value="POST">POST</option>
          <option value="PUT">PUT</option>
          <option value="PATCH">PATCH</option>
        </select>
      </Field>
      <Field label="Payload Mode">
        <select className="rb-select" value={(c.get("payloadTemplate") as string) ?? "full"} onChange={(e) => c.set("payloadTemplate", e.target.value)}>
          <option value="full">Full Document</option>
          <option value="fields_only">Fields Only</option>
        </select>
      </Field>
      <Field label="Auth Type">
        <select className="rb-select" value={(auth.type as string) ?? "none"} onChange={(e) => c.set("auth", { type: e.target.value })}>
          <option value="none">None</option>
          <option value="bearer">Bearer Token</option>
          <option value="basic">Basic Auth</option>
          <option value="api_key">API Key</option>
        </select>
      </Field>
      {auth.type === "bearer" && (
        <Field label="Token">
          <input className="rb-input" type="password" value={(auth.token as string) ?? ""} onChange={(e) => c.setNested("auth", "token", e.target.value)} />
        </Field>
      )}
      {auth.type === "basic" && (
        <>
          <Field label="Username">
            <input className="rb-input" value={(auth.username as string) ?? ""} onChange={(e) => c.setNested("auth", "username", e.target.value)} />
          </Field>
          <Field label="Password">
            <input className="rb-input" type="password" value={(auth.password as string) ?? ""} onChange={(e) => c.setNested("auth", "password", e.target.value)} />
          </Field>
        </>
      )}
      {auth.type === "api_key" && (
        <>
          <Field label="Header Name">
            <input className="rb-input" value={(auth.headerName as string) ?? ""} onChange={(e) => c.setNested("auth", "headerName", e.target.value)} placeholder="X-Api-Key" />
          </Field>
          <Field label="Key">
            <input className="rb-input" type="password" value={(auth.key as string) ?? ""} onChange={(e) => c.setNested("auth", "key", e.target.value)} />
          </Field>
        </>
      )}
      <Field label="Timeout (ms)">
        <input className="rb-input" type="number" value={(c.get("timeoutMs") as number) ?? 30000} onChange={(e) => c.set("timeoutMs", Number(e.target.value))} />
      </Field>
      <HeadersEditor headers={(c.get("headers") as Record<string, string>) ?? {}} onChange={(h) => c.set("headers", h)} />
    </div>
  );
}

// ─── Filesystem ───────────────────────────────────────────────

function FilesystemForm({ config, onChange }: { config: Record<string, unknown>; onChange: (c: Record<string, unknown>) => void }) {
  const c = useConfig(config, onChange);

  return (
    <div className="adapter-config-form">
      {/* WU-9.2 (R-53 secondary) — make ephemeral-disk semantics impossible to miss.
          Capture runs on Railway in cloud deployments; the container disk is
          wiped on every redeploy and partners can't read it. */}
      <div className="adapter-config-form__warning" role="note">
        <strong>⚠ Ephemeral storage.</strong> Filesystem destinations write to
        the container's local disk. On Railway-deployed Capture, files vanish on
        every redeploy and partners cannot access them. Use this only for
        self-hosted Capture deployments with a mounted volume.
      </div>
      <Field label="Base Path">
        <input className="rb-input" value={(c.get("basePath") as string) ?? ""} onChange={(e) => c.set("basePath", e.target.value)} placeholder="/mnt/exports" />
      </Field>
      <TemplateInput
        label="Folder Template"
        value={(c.get("folderTemplate") as string) ?? ""}
        onChange={(v) => c.set("folderTemplate", v)}
        placeholder="{classification}/{yyyy}/{MM}"
      />
      <TemplateInput
        label="Filename Template"
        value={(c.get("filenameTemplate") as string) ?? ""}
        onChange={(v) => c.set("filenameTemplate", v)}
        placeholder="{documentId}.{ext}"
      />
      <Field label="Create Directories">
        <input type="checkbox" checked={c.get("createDirectories") !== false} onChange={(e) => c.set("createDirectories", e.target.checked)} />
      </Field>
      <Field label="Overwrite Existing">
        <input type="checkbox" checked={!!c.get("overwriteExisting")} onChange={(e) => c.set("overwriteExisting", e.target.checked)} />
      </Field>
    </div>
  );
}

// ─── SFTP ─────────────────────────────────────────────────────

function SftpForm({ config, onChange }: { config: Record<string, unknown>; onChange: (c: Record<string, unknown>) => void }) {
  const c = useConfig(config, onChange);
  const auth = (c.get("auth") as Record<string, unknown>) ?? {};

  return (
    <div className="adapter-config-form">
      <p className="adapter-config-form__credentials-note">
        Credentials paste-once here. A future release will move these to a
        shared tenant credential vault (see R-53).
      </p>
      <Field label="Host">
        <input className="rb-input" value={(c.get("host") as string) ?? ""} onChange={(e) => c.set("host", e.target.value)} placeholder="sftp.example.com" />
      </Field>
      <Field label="Port">
        <input className="rb-input" type="number" value={(c.get("port") as number) ?? 22} onChange={(e) => c.set("port", Number(e.target.value))} />
      </Field>
      <Field label="Username">
        <input className="rb-input" value={(c.get("username") as string) ?? ""} onChange={(e) => c.set("username", e.target.value)} />
      </Field>
      <Field label="Auth Type">
        <select className="rb-select" value={(auth.type as string) ?? "password"} onChange={(e) => c.set("auth", { type: e.target.value })}>
          <option value="password">Password</option>
          <option value="privateKey">Private Key</option>
        </select>
      </Field>
      {auth.type === "password" && (
        <Field label="Password">
          <input className="rb-input" type="password" value={(auth.password as string) ?? ""} onChange={(e) => c.setNested("auth", "password", e.target.value)} />
        </Field>
      )}
      {auth.type === "privateKey" && (
        <>
          <Field label="Private Key">
            <textarea className="rb-input" rows={4} value={(auth.privateKey as string) ?? ""} onChange={(e) => c.setNested("auth", "privateKey", e.target.value)} placeholder="-----BEGIN RSA PRIVATE KEY-----" />
          </Field>
          <Field label="Passphrase">
            <input className="rb-input" type="password" value={(auth.passphrase as string) ?? ""} onChange={(e) => c.setNested("auth", "passphrase", e.target.value)} />
          </Field>
        </>
      )}
      <Field label="Remote Path">
        <input className="rb-input" value={(c.get("remotePath") as string) ?? ""} onChange={(e) => c.set("remotePath", e.target.value)} placeholder="/uploads/capture" />
      </Field>
      <TemplateInput
        label="Folder Template"
        value={(c.get("folderTemplate") as string) ?? ""}
        onChange={(v) => c.set("folderTemplate", v)}
        placeholder="{classification}/{yyyy}/{MM}"
      />
      <TemplateInput
        label="Filename Template"
        value={(c.get("filenameTemplate") as string) ?? ""}
        onChange={(v) => c.set("filenameTemplate", v)}
        placeholder="{documentId}.{ext}"
      />
    </div>
  );
}

// ─── S3 ───────────────────────────────────────────────────────

function S3Form({ config, onChange }: { config: Record<string, unknown>; onChange: (c: Record<string, unknown>) => void }) {
  const c = useConfig(config, onChange);

  return (
    <div className="adapter-config-form">
      <p className="adapter-config-form__credentials-note">
        Credentials paste-once here. A future release will move these to a
        shared tenant credential vault (see R-53). For MinIO / Cloudflare R2,
        fill in the optional <code>Endpoint</code> field below.
      </p>
      <Field label="Bucket">
        <input className="rb-input" value={(c.get("bucket") as string) ?? ""} onChange={(e) => c.set("bucket", e.target.value)} placeholder="my-export-bucket" />
      </Field>
      <Field label="Region">
        <input className="rb-input" value={(c.get("region") as string) ?? ""} onChange={(e) => c.set("region", e.target.value)} placeholder="us-east-1" />
      </Field>
      <Field label="Endpoint (MinIO/R2)">
        <input className="rb-input" value={(c.get("endpoint") as string) ?? ""} onChange={(e) => c.set("endpoint", e.target.value)} placeholder="https://s3.example.com (optional)" />
      </Field>
      <Field label="Access Key ID">
        <input className="rb-input" type="password" value={(c.get("accessKeyId") as string) ?? ""} onChange={(e) => c.set("accessKeyId", e.target.value)} />
      </Field>
      <Field label="Secret Access Key">
        <input className="rb-input" type="password" value={(c.get("secretAccessKey") as string) ?? ""} onChange={(e) => c.set("secretAccessKey", e.target.value)} />
      </Field>
      <Field label="Force Path Style">
        <input type="checkbox" checked={!!c.get("forcePathStyle")} onChange={(e) => c.set("forcePathStyle", e.target.checked)} />
      </Field>
      <Field label="Key Prefix">
        <input className="rb-input" value={(c.get("prefix") as string) ?? ""} onChange={(e) => c.set("prefix", e.target.value)} placeholder="capture-exports/" />
      </Field>
      <TemplateInput
        label="Folder Template"
        value={(c.get("folderTemplate") as string) ?? ""}
        onChange={(v) => c.set("folderTemplate", v)}
        placeholder="{classification}/{yyyy}/{MM}"
      />
      <TemplateInput
        label="Filename Template"
        value={(c.get("filenameTemplate") as string) ?? ""}
        onChange={(v) => c.set("filenameTemplate", v)}
        placeholder="{documentId}.{ext}"
      />
      <Field label="Storage Class">
        <select className="rb-select" value={(c.get("storageClass") as string) ?? ""} onChange={(e) => c.set("storageClass", e.target.value || undefined)}>
          <option value="">Default</option>
          <option value="STANDARD">Standard</option>
          <option value="STANDARD_IA">Standard-IA</option>
          <option value="GLACIER">Glacier</option>
          <option value="DEEP_ARCHIVE">Deep Archive</option>
        </select>
      </Field>
    </div>
  );
}

// ─── PDF Summary ──────────────────────────────────────────────

function PdfSummaryForm({ config, onChange }: { config: Record<string, unknown>; onChange: (c: Record<string, unknown>) => void }) {
  const c = useConfig(config, onChange);

  return (
    <div className="adapter-config-form">
      <Field label="Storage Path Template">
        <input className="rb-input" value={(c.get("storagePath") as string) ?? ""} onChange={(e) => c.set("storagePath", e.target.value)} placeholder="exports/pdf/{classification}/{yyyy}/{MM}" />
      </Field>
      <Field label="Filename Template">
        <input className="rb-input" value={(c.get("filenameTemplate") as string) ?? ""} onChange={(e) => c.set("filenameTemplate", e.target.value)} placeholder="{documentId}-summary.pdf" />
      </Field>
      <Field label="Include Images">
        <input type="checkbox" checked={c.get("includeImages") !== false} onChange={(e) => c.set("includeImages", e.target.checked)} />
      </Field>
      <Field label="Include Audit Trail">
        <input type="checkbox" checked={!!c.get("includeAuditTrail")} onChange={(e) => c.set("includeAuditTrail", e.target.checked)} />
      </Field>
      <Field label="Include OCR Confidence">
        <input type="checkbox" checked={!!c.get("includeOcrConfidence")} onChange={(e) => c.set("includeOcrConfidence", e.target.checked)} />
      </Field>
      <Field label="Page Size">
        <select className="rb-select" value={(c.get("pageSize") as string) ?? "letter"} onChange={(e) => c.set("pageSize", e.target.value)}>
          <option value="letter">Letter</option>
          <option value="a4">A4</option>
        </select>
      </Field>
      <Field label="Title">
        <input className="rb-input" value={(c.get("title") as string) ?? ""} onChange={(e) => c.set("title", e.target.value)} placeholder="Document Export Summary" />
      </Field>
    </div>
  );
}

// ─── Email ───────────────────────────────────────────────────

function EmailForm({ config, onChange }: { config: Record<string, unknown>; onChange: (c: Record<string, unknown>) => void }) {
  const c = useConfig(config, onChange);
  const smtp = (c.get("smtp") as Record<string, unknown>) ?? {};
  const smtpAuth = (smtp.auth as Record<string, unknown>) ?? {};

  const setSmtp = (key: string, value: unknown) => {
    onChange({ ...config, smtp: { ...smtp, [key]: value } });
  };
  const setSmtpAuth = (key: string, value: unknown) => {
    onChange({ ...config, smtp: { ...smtp, auth: { ...smtpAuth, [key]: value } } });
  };

  return (
    <div className="adapter-config-form">
      <Field label="SMTP Host">
        <input className="rb-input" value={(smtp.host as string) ?? ""} onChange={(e) => setSmtp("host", e.target.value)} placeholder="smtp.example.com" />
      </Field>
      <Field label="SMTP Port">
        <input className="rb-input" type="number" value={(smtp.port as number) ?? 587} onChange={(e) => setSmtp("port", Number(e.target.value))} />
      </Field>
      <Field label="Secure (TLS)">
        <input type="checkbox" checked={smtp.secure === true} onChange={(e) => setSmtp("secure", e.target.checked)} />
      </Field>
      <Field label="SMTP Username">
        <input className="rb-input" value={(smtpAuth.user as string) ?? ""} onChange={(e) => setSmtpAuth("user", e.target.value)} />
      </Field>
      <Field label="SMTP Password">
        <input className="rb-input" type="password" value={(smtpAuth.pass as string) ?? ""} onChange={(e) => setSmtpAuth("pass", e.target.value)} />
      </Field>
      <Field label="From Address">
        <input className="rb-input" value={(c.get("from") as string) ?? ""} onChange={(e) => c.set("from", e.target.value)} placeholder="capture@company.com" />
      </Field>
      <Field label="To Addresses">
        <input className="rb-input" value={((c.get("to") as string[]) ?? []).join(", ")} onChange={(e) => c.set("to", e.target.value.split(",").map(s => s.trim()).filter(Boolean))} placeholder="user@company.com, admin@company.com" />
      </Field>
      <Field label="CC">
        <input className="rb-input" value={((c.get("cc") as string[]) ?? []).join(", ")} onChange={(e) => { const v = e.target.value.split(",").map(s => s.trim()).filter(Boolean); c.set("cc", v.length > 0 ? v : undefined); }} placeholder="Optional" />
      </Field>
      <Field label="BCC">
        <input className="rb-input" value={((c.get("bcc") as string[]) ?? []).join(", ")} onChange={(e) => { const v = e.target.value.split(",").map(s => s.trim()).filter(Boolean); c.set("bcc", v.length > 0 ? v : undefined); }} placeholder="Optional" />
      </Field>
      <Field label="Subject Template">
        <input className="rb-input" value={(c.get("subject") as string) ?? ""} onChange={(e) => c.set("subject", e.target.value)} placeholder="Capture Export: {classification} — {documentId}" />
      </Field>
      <Field label="Body Format">
        <select className="rb-select" value={(c.get("bodyTemplate") as string) ?? "table"} onChange={(e) => c.set("bodyTemplate", e.target.value)}>
          <option value="table">HTML Table</option>
          <option value="json">JSON</option>
          <option value="plain">Plain Text</option>
        </select>
      </Field>
      <Field label="Attach Fields as JSON">
        <input type="checkbox" checked={!!c.get("attachFields")} onChange={(e) => c.set("attachFields", e.target.checked)} />
      </Field>
    </div>
  );
}

// ─── Dossier (document repository) ───────────────────────────

function DossierForm({ config, onChange }: { config: Record<string, unknown>; onChange: (c: Record<string, unknown>) => void }) {
  const c = useConfig(config, onChange);

  return (
    <div className="adapter-config-form">
      <Field label="Dossier Document Type">
        <input
          className="rb-input"
          value={(c.get("detectedTypeName") as string) ?? ""}
          onChange={(e) => c.set("detectedTypeName", e.target.value)}
          placeholder="e.g. invoice"
          required
        />
      </Field>
      <TemplateInput
        label="Title Template"
        value={(c.get("titleTemplate") as string) ?? ""}
        onChange={(v) => c.set("titleTemplate", v)}
        placeholder="{customDisplayLabel}"
        tokens={DOSSIER_TITLE_TOKENS}
      />
      <Field label="Scanner-Operator Header Field">
        <input
          className="rb-input"
          value={(c.get("scannerOperatorHeaderKey") as string) ?? ""}
          onChange={(e) => c.set("scannerOperatorHeaderKey", e.target.value)}
          placeholder="e.g. operator_id"
        />
      </Field>
      <DossierServiceUserPicker
        value={(c.get("serviceUserId") as string) ?? ""}
        onChange={(v) => c.set("serviceUserId", v)}
      />
      <p className="adapter-config-form__credentials-note">
        <strong>Dossier Document Type</strong> is the existing Dossier type these
        documents are filed under (required). <strong>Title Template</strong> sets
        the Dossier document title (e.g. {"{customDisplayLabel}"}).{" "}
        <strong>Scanner-Operator Header Field</strong> names the batch-header field
        holding the scanner operator&apos;s user id (for attribution), and{" "}
        <strong>Service User</strong> is the fallback Dossier user used when no
        scanner operator is on the batch header. Field mappings (the Mapping tab)
        become the document&apos;s indexed metadata in Dossier.
      </p>
    </div>
  );
}

/**
 * Lane D — Service User picker. Replaces the free-text cuid input (operators
 * couldn't know the opaque Dossier `User.id`) with a select of the tenant's
 * named Dossier service accounts, each labelled by name + source (e.g.
 * "Inbound Channels (inbound)"). Auto-defaults to the tenant default
 * (`sourceLabel === 'inbound'`, else the first account) when nothing is set yet.
 * A saved id that isn't in the returned list is preserved as a raw fallback
 * option so existing configs don't break, and manual entry is offered so a
 * value can still be set if the lookup is unavailable or a specific id is needed.
 */
function DossierServiceUserPicker({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  const [accounts, setAccounts] = useState<DossierServiceAccount[] | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  // Manual mode: free-text entry. Auto-on when the lookup fails, or when a
  // stored value isn't in the fetched list (e.g. a legacy hand-typed id).
  const [manual, setManual] = useState(false);

  useEffect(() => {
    let cancelled = false;
    listServiceAccounts()
      .then((res) => {
        if (cancelled) return;
        setAccounts(res.serviceAccounts);
        // Auto-default to the tenant default (inbound) when nothing is set yet.
        if (!value) {
          const fallback =
            res.serviceAccounts.find((a) => a.sourceLabel === "inbound") ??
            res.serviceAccounts[0];
          if (fallback) onChange(fallback.id);
        }
      })
      .catch(() => {
        if (cancelled) return;
        setLoadFailed(true);
        setManual(true);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The lookup-failure path already flips `manual` on (in the catch), so manual
  // mode is the single source of truth for the free-text fallback.
  const valueInList = !!accounts && accounts.some((a) => a.id === value);
  // Back-compat: a saved id that isn't in the returned list is still shown as a
  // raw fallback option so existing configs keep working.
  const showRawFallback = !!accounts && !valueInList && !!value;

  function labelFor(a: DossierServiceAccount): string {
    const base = a.name?.trim() || a.id;
    const tags = [a.sourceLabel?.trim() || null, a.canCreate ? null : "no create"].filter(Boolean);
    return tags.length > 0 ? `${base} (${tags.join(", ")})` : base;
  }

  return (
    <Field label="Service User">
      {manual ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 4, flex: 1 }}>
          <input
            className="rb-input"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Dossier user id (cuid)"
            aria-label="Service user id (manual)"
          />
          {loadFailed ? (
            <span style={{ fontSize: 11, color: "var(--color-text-muted)" }}>
              Couldn&apos;t load the tenant&apos;s Dossier service accounts — enter a user id manually.
            </span>
          ) : (
            accounts && (
              <button
                type="button"
                onClick={() => setManual(false)}
                style={{
                  alignSelf: "flex-start",
                  fontSize: 11,
                  background: "none",
                  border: "none",
                  color: "var(--color-accent, #ea580c)",
                  cursor: "pointer",
                  padding: 0,
                }}
              >
                ← pick from list
              </button>
            )
          )}
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 4, flex: 1 }}>
          <select
            className="rb-select"
            aria-label="Service user"
            value={value}
            onChange={(e) => onChange(e.target.value)}
          >
            {!value && (
              <option value="">{accounts ? "Select a service account…" : "Loading service accounts…"}</option>
            )}
            {accounts?.map((a) => (
              <option key={a.id} value={a.id}>
                {labelFor(a)}
              </option>
            ))}
            {showRawFallback && (
              // Back-compat: a saved id not in the returned list stays selectable.
              <option value={value}>{value} (saved)</option>
            )}
          </select>
          {showRawFallback && (
            <span style={{ fontSize: 11, color: "var(--color-warning, #FFC107)" }}>
              The stored service account isn&apos;t in this tenant&apos;s Dossier — it may 403 at export.
            </span>
          )}
          <button
            type="button"
            onClick={() => setManual(true)}
            style={{
              alignSelf: "flex-start",
              fontSize: 11,
              background: "none",
              border: "none",
              color: "var(--color-accent, #ea580c)",
              cursor: "pointer",
              padding: 0,
            }}
          >
            enter id manually
          </button>
        </div>
      )}
    </Field>
  );
}

// ─── Document Repository ──────────────────────────────────────

function DocumentRepositoryForm({ config, onChange }: { config: Record<string, unknown>; onChange: (c: Record<string, unknown>) => void }) {
  const c = useConfig(config, onChange);
  const packaging = (c.get("packaging") as string) ?? "pdf";
  const connectionId = (c.get("connectionId") as string) ?? "";
  const libraryName = (c.get("libraryName") as string) ?? "";
  const folderPath = (c.get("folderPath") as string) ?? "";

  const [connections, setConnections] = useState<ConnectionDto[]>([]);

  useEffect(() => {
    listConnections("sharepoint")
      .then((list) => setConnections(list.filter((cn) => cn.isActive)))
      .catch(() => setConnections([]));
  }, []);

  const selectedConnection = connections.find((cn) => cn.id === connectionId);

  return (
    <div className="adapter-config-form">
      <Field label="Packaging">
        <select
          className="rb-select"
          aria-label="Packaging"
          value={packaging}
          onChange={(e) => c.set("packaging", e.target.value)}
        >
          <option value="pdf">PDF (multipage)</option>
          <option value="tiff" disabled>TIFF (coming soon)</option>
          <option value="original" disabled>Original passthrough (coming soon)</option>
        </select>
      </Field>
      <TemplateInput
        label="Title Template"
        value={(c.get("titleTemplate") as string) ?? ""}
        onChange={(v) => c.set("titleTemplate", v)}
        placeholder="{customDisplayLabel}"
        tokens={DOSSIER_TITLE_TOKENS}
      />
      <Field label="Connection">
        <select
          className="rb-select"
          aria-label="Connection"
          value={connectionId}
          onChange={(e) => c.set("connectionId", e.target.value || undefined)}
        >
          <option value="">(none — dry-run preview)</option>
          {connections.map((cn) => (
            <option key={cn.id} value={cn.id}>
              {cn.name}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Document library">
        <input
          className="rb-input"
          aria-label="Document library"
          value={libraryName}
          onChange={(e) => c.set("libraryName", e.target.value)}
          placeholder="Documents"
        />
      </Field>
      <Field label="Folder">
        <input
          className="rb-input"
          aria-label="Folder"
          value={folderPath}
          onChange={(e) => c.set("folderPath", e.target.value)}
          placeholder="Invoices/2026"
        />
      </Field>
      {selectedConnection ? (
        <p className="adapter-config-form__credentials-note">
          Delivers each document to <strong>{selectedConnection.name}</strong>{" "}
          &rarr; {libraryName || "Documents"}/{folderPath}.
          The <strong>Mapping</strong> tab defines the index fields sent to the
          repository (each target column is a target index field; map document
          fields plus system fields like <code>_scanId</code> / <code>_jobTypeName</code>).
        </p>
      ) : (
        <p className="adapter-config-form__credentials-note">
          This destination runs as a <strong>preview (dry-run)</strong>: each document
          is assembled and its index metadata mapped, then recorded without being sent
          anywhere — so you can verify what and how you&apos;re exporting before a
          connection exists. <strong>Packaging</strong> selects the document artifact
          (PDF today). The <strong>Mapping</strong> tab defines the index fields sent to
          the repository (each target column is a target index field; map document
          fields plus system fields like <code>_scanId</code> / <code>_jobTypeName</code>).
          A real connection (SharePoint / FileNet / OneDrive) can be wired later.
        </p>
      )}
    </div>
  );
}

// ─── SQL ──────────────────────────────────────────────────────

function SqlForm({ config, onChange }: { config: Record<string, unknown>; onChange: (c: Record<string, unknown>) => void }) {
  const c = useConfig(config, onChange);
  const writeMode = (c.get("writeMode") as string) ?? "insert";
  const dialect = (c.get("dialect") as string) ?? "postgres";
  const upsertKey = c.get("upsertKey");
  const upsertKeyText = Array.isArray(upsertKey) ? (upsertKey as string[]).join(", ") : "";
  const connectionId = (c.get("connectionId") as string) ?? "";

  const [connections, setConnections] = useState<ConnectionDto[]>([]);

  useEffect(() => {
    listConnections("postgres")
      .then((list) => setConnections(list.filter((cn) => cn.isActive)))
      .catch(() => setConnections([]));
  }, []);

  const selectedConnection = connections.find((cn) => cn.id === connectionId);

  return (
    <div className="adapter-config-form">
      <p className="adapter-config-form__credentials-note" data-testid="sql-one-table-note">
        One sql destination writes one table. To split data across tables,
        create one sql destination per table, each with its own column
        mapping.
      </p>
      <Field label="Table">
        <input
          className="rb-input"
          aria-label="Table"
          value={(c.get("table") as string) ?? ""}
          onChange={(e) => c.set("table", e.target.value)}
          placeholder="e.g. invoices or public.invoices"
        />
      </Field>
      <p className="adapter-config-form__credentials-note" data-testid="sql-table-must-exist-note">
        The table must already exist, with column names matching the bound
        format's (or inline mapping's) columns. A mismatch is not caught here
        — it surfaces only as a failed export record at delivery.
      </p>
      <Field label="Write Mode">
        <select
          className="rb-select"
          aria-label="Write Mode"
          value={writeMode}
          onChange={(e) => c.set("writeMode", e.target.value)}
        >
          <option value="insert">Insert</option>
          <option value="upsert">Upsert (insert or update on conflict)</option>
        </select>
      </Field>
      {writeMode === "upsert" && (
        <Field label="Upsert Key">
          <input
            className="rb-input"
            aria-label="Upsert Key"
            value={upsertKeyText}
            onChange={(e) =>
              c.set(
                "upsertKey",
                e.target.value.split(",").map((s) => s.trim()).filter((s) => s.length > 0),
              )
            }
            placeholder="conflict columns, comma-separated (e.g. doc_number)"
          />
        </Field>
      )}
      <Field label="Dialect">
        <select
          className="rb-select"
          aria-label="Dialect"
          value={dialect}
          onChange={(e) => c.set("dialect", e.target.value)}
        >
          <option value="postgres">PostgreSQL</option>
          <option value="mysql" disabled>MySQL (coming soon)</option>
          <option value="sqlserver" disabled>SQL Server (coming soon)</option>
        </select>
      </Field>
      <Field label="Connection">
        <select
          className="rb-select"
          aria-label="Connection"
          value={connectionId}
          onChange={(e) => c.set("connectionId", e.target.value || undefined)}
        >
          <option value="">(none — dry-run preview)</option>
          {connections.map((cn) => (
            <option key={cn.id} value={cn.id}>
              {cn.name} ({String(cn.config.host ?? "")}:{String(cn.config.port ?? "")}/{String(cn.config.database ?? "")})
            </option>
          ))}
        </select>
      </Field>
      {selectedConnection ? (
        <p className="adapter-config-form__credentials-note">
          Delivers each row to <strong>{selectedConnection.name}</strong>.
          The <strong>Mapping</strong> tab defines the columns (each target column is a table
          column; values are always bound parameters).
        </p>
      ) : (
        <p className="adapter-config-form__credentials-note">
          This destination runs as a <strong>preview (dry-run)</strong>: each document&apos;s
          mapped row is turned into a parameterized <code>INSERT</code>/<code>UPSERT</code>
          and recorded without being executed — so you can verify the generated SQL before a
          database connection exists. The <strong>Mapping</strong> tab defines the columns
          (each target column is a table column; values are always bound parameters). A real
          database connection can be wired later.
        </p>
      )}
    </div>
  );
}

// ─── Headers Editor (for webhook) ─────────────────────────────

function HeadersEditor({ headers, onChange }: { headers: Record<string, string>; onChange: (h: Record<string, string>) => void }) {
  const [entries, setEntries] = useState<Array<[string, string]>>(
    Object.entries(headers).length > 0 ? Object.entries(headers) : [],
  );

  const update = (newEntries: Array<[string, string]>) => {
    setEntries(newEntries);
    const obj: Record<string, string> = {};
    for (const [k, v] of newEntries) {
      if (k) obj[k] = v;
    }
    onChange(obj);
  };

  return (
    <div className="rb-row" style={{ flexDirection: "column", alignItems: "stretch" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <label className="rb-label">Custom Headers</label>
        <button
          type="button"
          className="rb-btn"
          onClick={() => update([...entries, ["", ""]])}
          style={{ fontSize: 12, padding: "2px 8px" }}
        >
          + Add
        </button>
      </div>
      {entries.map(([key, val], idx) => (
        <div key={idx} style={{ display: "flex", gap: 8, marginTop: 4 }}>
          <input
            className="rb-input"
            value={key}
            onChange={(e) => {
              const next = [...entries] as Array<[string, string]>;
              next[idx] = [e.target.value, val];
              update(next);
            }}
            placeholder="Header name"
            style={{ flex: 1 }}
          />
          <input
            className="rb-input"
            value={val}
            onChange={(e) => {
              const next = [...entries] as Array<[string, string]>;
              next[idx] = [key, e.target.value];
              update(next);
            }}
            placeholder="Value"
            style={{ flex: 1 }}
          />
          <button
            type="button"
            className="rb-btn rb-btn-danger"
            onClick={() => update(entries.filter((_, i) => i !== idx))}
            style={{ padding: "2px 8px" }}
          >
            x
          </button>
        </div>
      ))}
    </div>
  );
}
